import { Box, Button, CircularProgress, Tab, Tabs, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { useCreateReport } from '_api/mutations/report';
import { Encounter, EncounterPayload, EncounterTimeFilter } from '_api/types';
import { PatientRouteMap, converToDayjs } from '_constants';
import { ENCOUNTER_TIME_FILTER } from '_constants/derived';
import { DefaultEncounter } from '_constants/object-templates';
import { ActionsFlexBox, PatientProfileTable } from 'components/molecules';
import { EncounterModal } from 'components/organisms/modals';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc'; // Import the UTC plugin
import { MRT_ColumnDef } from 'material-react-table';
import React, { useMemo, useState, useRef, MutableRefObject } from 'react';
import { FaChartBar } from 'react-icons/fa6';
import { useNavigateTo } from 'utils';
import { useEncounter, useCustomEffect } from 'utils/hooks';

import { PatientEncounterTableProps } from './types';

dayjs.extend(utc);

const EncounterTransform = (encounter: Encounter) => {
  return {
    ...encounter,
    date_of_observation: dayjs(encounter.date_of_observation).format('MM/DD/YYYY'),
  } as EncounterPayload;
};

export const PatientEncounterTable = ({
  patientId,
  therapistId,
}: PatientEncounterTableProps) => {
  const {
    customFilter,
    dailyEncounters,
    encounters: encountersData,
    isLoading,
    monthlyEncounters,
    weeklyEncounters,
  } = useEncounter({ patientId });
  const [encounters, setEncounters] = useState(dailyEncounters);
  const [encounter, setEncounter] = useState(DefaultEncounter);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigateTo();
  const [timeFrame, setTimeFrame] = useState<EncounterTimeFilter | undefined>(
    ENCOUNTER_TIME_FILTER.DAILY,
  );

  const today = dayjs();
  const weekly = dayjs().subtract(1, 'week');
  const monthly = dayjs().subtract(1, 'day').subtract(1, 'month');
  const startingDate = useRef<Dayjs>(today);
  const endingDate = useRef<Dayjs>(today);

  const [startDate, setStartDate] = useState<Dayjs | null>(
    converToDayjs(dayjs().subtract(1, 'month').format('MM-DD-YYYY')),
  );
  const [endDate, setEndDate] = useState<Dayjs | null>(
    converToDayjs(dayjs().format('MM-DD-YYYY')),
  );
  const [open, setOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const { mutateAsync: createReport } = useCreateReport();

  useCustomEffect(() => {
    switch (timeFrame) {
      case ENCOUNTER_TIME_FILTER.DAILY:
        setEncounters(dailyEncounters);
        startingDate.current = today.subtract(1, 'day');
        endingDate.current = startingDate.current;
        break;
      case ENCOUNTER_TIME_FILTER.WEEKLY:
        setEncounters(weeklyEncounters);
        startingDate.current = weekly;
        endingDate.current = today.subtract(1, 'day');
        break;
      case ENCOUNTER_TIME_FILTER.MONTHLY:
        setEncounters(monthlyEncounters);
        startingDate.current = monthly;
        endingDate.current = today.subtract(1, 'day');
        break;
      case ENCOUNTER_TIME_FILTER.CUSTOM:
        if (startDate && endDate) {
          setEncounters(customFilter(startDate.valueOf(), endDate.valueOf()));
          startingDate.current = startDate;
          endingDate.current = endDate;
        } else {
          setEncounters(encountersData);
        }
        break;
      default:
        setEncounters(encountersData);
    }
  }, [encountersData, timeFrame, startDate, endDate]);

  const close = () => {
    setIsEdit(false);
    setOpen(false);
    setEncounter(DefaultEncounter);
  };

  const handleTabChange = (
    _event: React.SyntheticEvent,
    newValue: EncounterTimeFilter,
  ) => {
    setTimeFrame(newValue);
  };

  const onClick = (enc: Encounter, edit: boolean) => {
    setEncounter(EncounterTransform(enc));
    setIsEdit(edit);
    setOpen(true);
  };

  const handleReport = async (
    startingDate: MutableRefObject<Dayjs>,
    endingDate: MutableRefObject<Dayjs>,
  ) => {
    try {
      const payload = {
        startDate: startingDate.current.format('YYYY-MM-DD'),
        endDate: endingDate.current.format('YYYY-MM-DD'),
        patientId: patientId,
        therapistId: therapistId,
      };

      const response = await createReport(payload);
      return response;
    } catch (error) {
      console.error('Error creating report:', error);
      return null;
    }
  };

  const handleOpenWindow = async (
    edit: boolean,
    startingDate: MutableRefObject<Dayjs>,
    endingDate: MutableRefObject<Dayjs>,
  ) => {
    setLoading(true);
    const reportData = await handleReport(startingDate, endingDate);
    const startingDateStr = startingDate.current.format('MM/DD/YYYY');
    const endingDateStr =
      timeFrame == ENCOUNTER_TIME_FILTER.DAILY
        ? ''
        : timeFrame == ENCOUNTER_TIME_FILTER.WEEKLY
          ? endingDate.current.format('MM/DD/YYYY')
          : timeFrame == ENCOUNTER_TIME_FILTER.MONTHLY
            ? endingDate.current.format('MM/DD/YYYY')
            : endingDate.current.format('MM/DD/YYYY');
    if (reportData) {
      navigate(PatientRouteMap.ENCOUNTER_REPORT, undefined, {
        startingDateStr,
        endingDateStr,
        reportData,
        isEditMode: edit,
      });
    } else {
      console.error('No report data available.');
    }
    setLoading(false);
  };

  const columnData = useMemo<MRT_ColumnDef<Encounter>[]>(
    () => [
      {
        header: 'Date',
        Cell: ({ row }) => {
          return dayjs(row.original.date_of_observation).format('MM/DD/YYYY');
        },
        size: 10,
      },
      {
        header: 'Time Spent',
        Cell: ({ row }) => `${row.original.minutes} minutes`,
        size: 50,
      },
      {
        header: 'Actions',
        accessorFn: (row) => (
          <ActionsFlexBox
            msg={'encounter'}
            viewOnClick={() => onClick(row, false)}
            editOnClick={() => onClick(row, true)}
            downloadOnClick={() => {}}
            openInNewOnClick={() => handleOpenWindow(false, startingDate, endingDate)}
            actionButtons={{
              viewButton: true,
              editButton: true,
              openNewTabButton: timeFrame == ENCOUNTER_TIME_FILTER.DAILY,
            }}
          />
        ),
        size: 10,
      },
    ],
    [timeFrame],
  );

  const isEmptyDailyEncounters =
    encounters && timeFrame == ENCOUNTER_TIME_FILTER.DAILY && encounters.length < 1;

  return (
    <>
      {encounters && timeFrame && !isLoading ? (
        <PatientProfileTable
          columns={columnData}
          data={!isLoading && encounters ? encounters : []}
          onAddClick={() => setOpen(true)}
          title="Encounters"
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              backgroundColor: '#0xFFF',
            }}
          >
            <Tabs
              value={timeFrame}
              onChange={handleTabChange}
              sx={{ marginBottom: '1rem' }}
            >
              <Tab label="Daily" value={ENCOUNTER_TIME_FILTER.DAILY} />
              <Tab label="Weekly" value={ENCOUNTER_TIME_FILTER.WEEKLY} />
              <Tab label="Monthly" value={ENCOUNTER_TIME_FILTER.MONTHLY} />
              <Tab label="Custom" value={ENCOUNTER_TIME_FILTER.CUSTOM} />
            </Tabs>

            {(timeFrame != ENCOUNTER_TIME_FILTER.DAILY || isEmptyDailyEncounters) && (
              <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                <Box sx={{ marginTop: '10px' }}>
                  {loading ? <CircularProgress size={30} /> : ''}
                </Box>
                <Button
                  sx={{ height: '40px', marginTop: '10px', fontWeight: 'bold' }}
                  onClick={() => handleOpenWindow(false, startingDate, endingDate)}
                >
                  Open Report
                  <Box sx={{ marginTop: '2px', paddingLeft: '5px', color: 'red' }}>
                    <FaChartBar />
                  </Box>
                </Button>
              </Box>
            )}
          </Box>
          {timeFrame !== ENCOUNTER_TIME_FILTER.CUSTOM ? null : (
            <Box style={{ marginBottom: 20, marginTop: 10 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  width: '67%',
                }}
              >
                <DatePicker
                  label="Start Date"
                  value={startDate}
                  onChange={setStartDate}
                  disableFuture={true}
                />
                <DatePicker
                  label="End Date"
                  value={endDate}
                  onChange={setEndDate}
                  disableFuture={true}
                />
              </Box>
              {startDate && endDate && startDate.valueOf() > endDate.valueOf() ? (
                <Typography variant="subtitle2" color={'error'}>
                  Error in your date range - End Date is before Start Date.
                </Typography>
              ) : null}
            </Box>
          )}
        </PatientProfileTable>
      ) : null}
      <EncounterModal
        patientId={patientId}
        open={open}
        initValue={encounter}
        editing={isEdit}
        onEdit={(value: boolean) => setIsEdit(value)}
        onClose={close}
        refetch={() => setEncounters([])}
      />
    </>
  );
};
